import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export const REVENUE_ADMIN_ROLE = 'admin';
export const REVENUE_VIEWER_ROLE = 'standard-rev-collection';

export type AppRole = 'standard' | typeof REVENUE_ADMIN_ROLE | typeof REVENUE_VIEWER_ROLE;

export type RevenueAccess = {
  role: AppRole;
  isAdmin: boolean;
  canViewRevenue: boolean;
  canMutateRevenue: boolean;
  loading: boolean;
};

/** In-memory cache so Header remounts / route changes don’t hide the nav while re-fetching. */
let roleCache: { email: string; role: AppRole } | null = null;

function parseAppRole(raw: unknown): AppRole {
  if (raw === REVENUE_ADMIN_ROLE) return REVENUE_ADMIN_ROLE;
  if (raw === REVENUE_VIEWER_ROLE) return REVENUE_VIEWER_ROLE;
  return 'standard';
}

function accessFromRole(role: AppRole): Omit<RevenueAccess, 'loading'> {
  const isAdmin = role === REVENUE_ADMIN_ROLE;
  const canViewRevenue = isAdmin || role === REVENUE_VIEWER_ROLE;
  return {
    role,
    isAdmin,
    canViewRevenue,
    canMutateRevenue: isAdmin,
  };
}

export function useRevenueAccess(): RevenueAccess {
  const { user } = useAuth();

  const [role, setRole] = useState<AppRole>(() => {
    if (!user?.email) return 'standard';
    return roleCache?.email === user.email ? roleCache.role : 'standard';
  });

  const [loading, setLoading] = useState(() => {
    if (!user?.email) return false;
    return roleCache?.email !== user.email;
  });

  useEffect(() => {
    if (!user?.email) {
      roleCache = null;
      setRole('standard');
      setLoading(false);
      return;
    }

    if (roleCache?.email === user.email) {
      setRole(roleCache.role);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setRole('standard');

    async function loadRole() {
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .ilike('email', user!.email!)
          .maybeSingle();

        const next = error ? 'standard' : parseAppRole(data?.role);
        if (!cancelled) {
          setRole(next);
          roleCache = { email: user!.email!, role: next };
        }
      } catch {
        if (!cancelled) {
          setRole('standard');
          roleCache = { email: user!.email!, role: 'standard' };
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadRole();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  return { ...accessFromRole(role), loading };
}
